import clsx from 'clsx'

import { htmlToText } from '@/shared/lib/string/html-to-text'
import { PageMeta } from '@/shared/lib/meta/page-meta'
import { PageBackButton } from '@/shared/ui/Layout/page/PageBackButton'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { RewardBadge } from '@/shared/ui/RewardBadge'
import type { JourneyWithUnderscores, StageWithUnderscores } from '@/types/journey'

type JourneyShowHeroProps = {
  journey: JourneyWithUnderscores & {
    _stages: StageWithUnderscores[]
    _progress: { finishedPercent?: number }
    _totalPointReward?: number
    _totalCoinReward?: number
  }
}

type RewardChipProps = {
  label: string
  pointReward?: number
  coinReward?: number
}

const JourneyRewardChip = ({
  label,
  pointReward = 0,
  coinReward = 0
}: RewardChipProps) => {
  if (pointReward <= 0 && coinReward <= 0) return null

  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-lg bg-black/25 px-2.5 py-1.5 backdrop-blur-[2px]">
      <span className="text-[0.625rem] font-bold uppercase tracking-wide text-white/70">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1.5 [&_.reward-badge__label]:text-white/90 [&_.reward-badge__value]:text-white">
        {pointReward > 0 && (
          <RewardBadge type="xp" value={pointReward} variant="inline" />
        )}
        {coinReward > 0 && (
          <RewardBadge type="coins" value={coinReward} variant="inline" />
        )}
      </div>
    </div>
  )
}

export const JourneyShowHero = ({ journey }: JourneyShowHeroProps) => {
  const coverUrl = journey.cover?.file?.url
  const description = journey.description
    ? htmlToText(journey.description)
    : ''

  const totalStageCount = journey._stages.length
  const finishedStageCount = journey._stages.filter(
    stage => stage._progress.finished
  ).length
  const journeyPercent = Math.round(
    (journey._progress?.finishedPercent ?? 0) * 100
  )

  const nextIncompleteStage =
    journey._stages.find(stage => !stage._progress.finished) ?? null

  const hasNextReward =
    (nextIncompleteStage?.coin_reward ?? 0) > 0 ||
    (nextIncompleteStage?.point_reward ?? 0) > 0

  const hasFinalReward =
    (journey.coin_reward ?? 0) > 0 || (journey.point_reward ?? 0) > 0

  const showPhaseProgress = totalStageCount > 1
  const showNextReward = showPhaseProgress && hasNextReward
  const showFinalReward = hasFinalReward
  const showRewardRow = showNextReward || showFinalReward

  return (
    <>
      <PageMeta title={journey.name} />
      <section className="relative overflow-hidden">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
            aria-hidden
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: 'var(--gradient-hero)' }}
            aria-hidden
          />
        )}

        <div
          className={clsx(
            'absolute inset-0',
            coverUrl
              ? 'bg-gradient-to-t from-[#12051f] via-[#12051f]/88 to-[#12051f]/35'
              : 'bg-gradient-to-t from-[#12051f]/95 via-transparent to-transparent'
          )}
          aria-hidden
        />

        <div className="relative mx-auto w-full max-w-2xl">
          <div className="flex items-center gap-3 px-4 pb-1 pt-4">
            <PageBackButton variant="immersive" backHref="/jornadas" />
            <h1 className="m-0 line-clamp-2 min-w-0 flex-1 text-lg font-bold leading-tight text-white">
              {journey.name}
            </h1>
          </div>

          <div className="space-y-2.5 px-4 pb-3.5 pt-1">
            {description ? (
              <p className="m-0 line-clamp-2 text-2xs leading-snug text-white/80">
                {description}
              </p>
            ) : null}

            {showPhaseProgress ? (
              <div>
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <span className="text-2xs font-semibold text-white/75">
                    {finishedStageCount} de {totalStageCount} fases concluídas
                  </span>
                  <span className="text-sm font-bold text-white">
                    {journeyPercent}%
                  </span>
                </div>
                <ProgressBar
                  value={journeyPercent}
                  size="sm"
                  className="[&>div]:bg-white/20 [&>div]:shadow-none [&>div>div]:bg-white"
                  aria-label="Progresso da jornada"
                />
              </div>
            ) : null}

            {showRewardRow ? (
              <div
                className={clsx(
                  'grid gap-2',
                  showNextReward && showFinalReward ? 'grid-cols-2' : 'grid-cols-1'
                )}
              >
                {showNextReward && nextIncompleteStage ? (
                  <JourneyRewardChip
                    label="Próxima"
                    pointReward={nextIncompleteStage.point_reward ?? 0}
                    coinReward={nextIncompleteStage.coin_reward ?? 0}
                  />
                ) : null}
                {showFinalReward ? (
                  <JourneyRewardChip
                    label="Recompensa final"
                    pointReward={journey.point_reward ?? 0}
                    coinReward={journey.coin_reward ?? 0}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </>
  )
}
